import type { JSX } from 'react';
import { DateTime } from '@ksyos/date-time';
import { Button, Card, Form, FormButtonRow, FormGroup, FormSection, IconButton, Switch } from '@ksyos/design-system';
import { Fragment, useEffect, useState } from 'react';
import type { AssetValidation } from '@common/validation/serviceOrganizationSetting';
import type {
    IEmbloomQuestionnaire,
    IServiceOrganizationGroupHistory,
    IServiceOrganizationSettings,
} from '@frontend/components/interfaces';
import type { IServiceGroupSetting } from '@frontend/components/interfaces/serviceGroupSetting';
import { ManagerAuthorization } from '@common/enums';
import { ServiceCode } from '@common/enums/serviceCode';
import { embloomQuestionnaireServiceOrganizationSettingSchema } from '@common/validation/embloomQuestionnaireServiceOrganizationSetting';
import { serviceOrganizationSettingAddEditSchema } from '@common/validation/serviceOrganizationSetting';
import { FormParagraphFormGroup } from '@frontend/components/fields/FormParagraphField';
import { hasOneAuthorization } from '@frontend/utils';
import { useServiceOrganizationSettingContext } from '@frontend/utils/hooks/useServiceOrganizationsSettingsContext';
import type { ValidationErrors } from '../../../../utils/validation';
import { fetchJson } from '../../../../store/fetch';
import { translateKey } from '../../../../translate';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '../../../../utils/validation';
import { FormContextProvider } from '../../../elements/formContext';
import { CancelButton } from '../../../userDataManagement/CancelButton';
import { SaveButton } from '../../../userDataManagement/SaveButton';
import { ServiceOrganizationGroupSettingChangedOrDeletedModal } from './ServiceOrganizationGroupSettingChangedOrDeletedModal';
import { ServiceOrganizationSettingSettingsList } from './ServiceOrganizationSettingSettingsList';
import { ServiceOrganizationSettingStopModal } from './ServiceOrganizationSettingStopModal';
import {
    findAndSetCurrentServiceGroup,
    isServiceOrganizationGroupHistoriesChanged,
    validateGroupHistoriesTimeOverlap,
    validateServiceGroupSettingId,
} from './utils/serviceOrganizationGroupHistoryUtils';

export enum ServiceOrganizationSettingFormMode {
    edit = 'edit',
    read = 'read',
}

export interface IServiceOrganizationSettingFormSectionProps extends IServiceOrganizationSettings {
    errors: ValidationErrors;
    handleChange: (
        key: keyof IServiceOrganizationSettings,
        value: IServiceOrganizationSettings[keyof IServiceOrganizationSettings]
    ) => void;
    hasBiometricians: boolean;
    hasServiceProviders: boolean;
    mode: ServiceOrganizationSettingFormMode;
    validateOne: (key: keyof IServiceOrganizationSettings) => void;
}

export interface IServiceGraderPoolOption {
    id: string;
    name: string;
}

export function ServiceOrganizationSetting(): JSX.Element {
    const { serviceOrganizationSetting, reloadServiceOrganizationSettings } = useServiceOrganizationSettingContext();
    const canEdit = hasOneAuthorization(ManagerAuthorization.organizationAddEdit);
    const [hasServiceProviders, setHasServiceProviders] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [mode, setMode] = useState<ServiceOrganizationSettingFormMode>(ServiceOrganizationSettingFormMode.read);
    const [serviceOrganizationSettingEditVersion, setServiceOrganizationSettingEditVersion] =
        useState<IServiceOrganizationSettings | null>(null);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [showStopServiceOrganizationSettingModal, setShowStopServiceOrganizationSettingModal] = useState(false);
    const [embloomQuestionnaires, setEmbloomQuestionnaires] = useState<
        { embloomQuestionnaire: IEmbloomQuestionnaire }[]
    >([]);
    const [containsServiceOrganizationSettings, setContainsServiceOrganizationSettings] = useState(false);
    const [currentServiceGroup, setCurrentServiceGroup] = useState<IServiceGroupSetting | null>(null);
    const [
        showServiceOrganizationGroupSettingChangedOrDeleteModal,
        setShowServiceOrganizationGroupSettingChangedOrDeleteModal,
    ] = useState(false);
    const [isServiceGroupChanged, setIsServiceGroupChanged] = useState(false);
    const [isServiceGroupDeleted, setIsServiceGroupDeleted] = useState(false);
    const [originalServiceGroupHistory, setOriginalServiceGroupHistory] = useState<IServiceOrganizationGroupHistory[]>(
        []
    );

    useEffect(() => {
        if (mode === ServiceOrganizationSettingFormMode.edit) {
            setServiceOrganizationSettingEditVersion(
                createServiceOrganizationSettingEditVersion({ ...serviceOrganizationSetting })
            );
            setIsServiceGroupChanged(false);
            setIsServiceGroupDeleted(false);
            setOriginalServiceGroupHistory(serviceOrganizationSetting.serviceOrganizationGroupHistory);
        } else {
            setServiceOrganizationSettingEditVersion(null);
            setErrors(emptyValidationErrors);
        }
    }, [mode]);

    useEffect(() => {
        if (serviceOrganizationSetting.service.code === ServiceCode.GGZOembl) {
            loadEmbloomQuestionnaires(serviceOrganizationSetting.id);
        }
    }, [serviceOrganizationSetting.service.code, mode]);

    useEffect(() => {
        findAndSetCurrentServiceGroup(
            serviceOrganizationSettingEditVersion?.serviceOrganizationGroupHistory ||
                serviceOrganizationSetting.serviceOrganizationGroupHistory ||
                [],
            setCurrentServiceGroup
        );
        setErrors(
            validateGroupHistoriesTimeOverlap(
                serviceOrganizationSettingEditVersion?.serviceOrganizationGroupHistory,
                errors
            )
        );
    }, [mode, JSON.stringify(serviceOrganizationSettingEditVersion?.serviceOrganizationGroupHistory)]);

    useEffect(() => {
        setErrors(
            validateServiceGroupSettingId(
                serviceOrganizationSetting.service.code,
                currentServiceGroup,
                hasServiceProviders,
                errors
            )
        );
    }, [currentServiceGroup]);

    return (
        <FormContextProvider
            value={{
                errors: emptyValidationErrors,
                item: serviceOrganizationSettingEditVersion || serviceOrganizationSetting,
                onChange: async (key, value) => handleChange(key, value),
                onDoValidate: async (key) => validateOne(key),
                placeholderGroupName: 'serviceOrganizationSettings',
            }}
        >
            <Card>
                <Card.Transition type={mode} />

                {canEdit ? (
                    <Card.Header
                        action={
                            showEditIcon() ? (
                                <IconButton
                                    icon="edit-light"
                                    labelText="edit"
                                    onClick={() => setMode(ServiceOrganizationSettingFormMode.edit)}
                                />
                            ) : undefined
                        }
                        title={`${translateKey('common.settings')} ${serviceOrganizationSetting.service.name}`}
                    />
                ) : (
                    <Card.Header
                        title={`${translateKey('common.settings')} ${serviceOrganizationSetting.service.name}`}
                    />
                )}
                <Card.Body>
                    <Form submitOnEnter={false}>
                        <FormSection title={translateKey('common.status')}>
                            <FormParagraphFormGroup label={translateKey('common.activeSince')}>
                                {DateTime.from(serviceOrganizationSetting.startDate).format('DD-MM-YYYY')}
                            </FormParagraphFormGroup>

                            <FormParagraphFormGroup label={translateKey('common.activeUntil')}>
                                {serviceOrganizationSetting.endDate
                                    ? DateTime.from(serviceOrganizationSetting.endDate).format('DD-MM-YYYY')
                                    : '-'}
                            </FormParagraphFormGroup>
                        </FormSection>

                        <Fragment key={`${serviceOrganizationSetting.serviceId}`}>
                            <ServiceOrganizationSettingSettingsList
                                currentServiceGroup={currentServiceGroup}
                                embloomQuestionnaires={embloomQuestionnaires}
                                errors={errors}
                                handleChange={handleChange}
                                hasServiceProviders={hasServiceProviders}
                                mode={mode}
                                serviceOrganizationSettingEditVersion={serviceOrganizationSettingEditVersion}
                                setContainsServiceOrganizationSettings={setContainsServiceOrganizationSettings}
                                setCurrentServiceGroup={setCurrentServiceGroup}
                                setEmbloomQuestionnaires={setEmbloomQuestionnaires}
                                setHasServiceProviders={setHasServiceProviders}
                                setMode={setMode}
                                validateOne={validateOne}
                            />
                        </Fragment>
                        {serviceOrganizationSetting.service.code === ServiceCode.TFO && (
                            <FormSection title={translateKey('serviceOrganizationSetting.validate')}>
                                {mode === ServiceOrganizationSettingFormMode.edit && (
                                    <>
                                        <FormGroup
                                            label={translateKey('serviceOrganizationSetting.validateAssetName')}
                                            layout="horizontal"
                                        >
                                            <Switch
                                                checked={
                                                    serviceOrganizationSettingEditVersion?.assetValidation.isEnabled ??
                                                    false
                                                }
                                                onChange={() => {
                                                    handleChange('assetValidation', {
                                                        isEnabled:
                                                            !serviceOrganizationSettingEditVersion?.assetValidation
                                                                .isEnabled,
                                                        type: serviceOrganizationSettingEditVersion?.assetValidation
                                                            .type,
                                                    } as AssetValidation);
                                                }}
                                            />
                                        </FormGroup>
                                        {serviceOrganizationSettingEditVersion?.assetValidation.isEnabled && (
                                            <FormParagraphFormGroup
                                                label={translateKey('serviceOrganizationSetting.validationOnField')}
                                            >
                                                {serviceOrganizationSettingEditVersion.assetValidation?.type}
                                            </FormParagraphFormGroup>
                                        )}
                                    </>
                                )}
                                {mode === ServiceOrganizationSettingFormMode.read && (
                                    <>
                                        <FormParagraphFormGroup
                                            label={translateKey('serviceOrganizationSetting.validateAssetName')}
                                        >
                                            {serviceOrganizationSetting.assetValidation?.isEnabled
                                                ? translateKey('common.yes')
                                                : translateKey('common.no')}
                                        </FormParagraphFormGroup>
                                        {serviceOrganizationSetting?.assetValidation.isEnabled && (
                                            <FormParagraphFormGroup
                                                label={translateKey('serviceOrganizationSetting.validationOnField')}
                                            >
                                                {serviceOrganizationSetting.assetValidation?.type}
                                            </FormParagraphFormGroup>
                                        )}
                                    </>
                                )}
                            </FormSection>
                        )}

                        {mode === ServiceOrganizationSettingFormMode.edit && (
                            <FormButtonRow>
                                <CancelButton
                                    disabled={isSaving}
                                    onCancel={async () => {
                                        setCurrentServiceGroup(null);
                                        await reloadServiceOrganizationSettings();
                                        setMode(ServiceOrganizationSettingFormMode.read);
                                    }}
                                />
                                <SaveButton
                                    disabled={isSaving}
                                    onSave={handleSubmit}
                                />
                            </FormButtonRow>
                        )}
                    </Form>
                </Card.Body>
                {!serviceOrganizationSetting.endDate && canEdit && (
                    <Card.Footer>
                        <Button
                            onClick={() => setShowStopServiceOrganizationSettingModal(true)}
                            variant="link"
                        >
                            {translateKey('service.stopService', serviceOrganizationSetting.service.name)}
                        </Button>
                    </Card.Footer>
                )}
            </Card>

            {showStopServiceOrganizationSettingModal && (
                <ServiceOrganizationSettingStopModal
                    onClose={() => {
                        setShowStopServiceOrganizationSettingModal(false);
                        setMode(ServiceOrganizationSettingFormMode.read);
                    }}
                    organizationName={serviceOrganizationSetting.organization.name}
                    reloadServiceOrganizationSetting={reloadServiceOrganizationSettings}
                    serviceOrganizationSettingIds={[serviceOrganizationSetting.id]}
                    serviceOrServiceGroupOrCarePathName={serviceOrganizationSetting.service.name}
                    startDate={serviceOrganizationSetting.startDate}
                />
            )}
            {showServiceOrganizationGroupSettingChangedOrDeleteModal && (
                <ServiceOrganizationGroupSettingChangedOrDeletedModal
                    isChanged={isServiceGroupChanged}
                    isDeleted={isServiceGroupDeleted}
                    isSaving={isSaving}
                    onClose={() => setShowServiceOrganizationGroupSettingChangedOrDeleteModal(false)}
                    onSave={async () => {
                        await handleSubmit();
                        setShowServiceOrganizationGroupSettingChangedOrDeleteModal(false);
                    }}
                />
            )}
        </FormContextProvider>
    );

    async function loadEmbloomQuestionnaires(serviceOrganizationSettingId: string) {
        const result = await fetchJson(
            `/api/serviceServiceOrganizationSettings/${serviceOrganizationSettingId}/embloomQuestionnaireServiceOrganizationSettings`,
            'GET'
        );
        setEmbloomQuestionnaires(result.json.items);
    }

    function handleChange<K extends keyof IServiceOrganizationSettings>(
        name: K,
        value: IServiceOrganizationSettings[K]
    ) {
        setServiceOrganizationSettingEditVersion({
            ...serviceOrganizationSettingEditVersion,
            [name]: value,
        } as IServiceOrganizationSettings);
    }

    function validateOne(name: keyof IServiceOrganizationSettings) {
        let updatedErrors = errors;
        updatedErrors = updateValidationErrorsForOne(
            serviceOrganizationSettingAddEditSchema,
            serviceOrganizationSettingEditVersion,
            updatedErrors,
            name
        );
        setErrors(updatedErrors);
    }

    function showEditIcon() {
        return mode === ServiceOrganizationSettingFormMode.read && canEdit && containsServiceOrganizationSettings;
    }

    async function handleSubmit() {
        if (serviceOrganizationSettingEditVersion) {
            setIsSaving(true);
            const itemToSave = createServiceOrganizationSettingToSave();

            const isGroupHistoriesChanged = isServiceOrganizationGroupHistoriesChanged(
                originalServiceGroupHistory,
                itemToSave.serviceOrganizationGroupHistory,
                setIsServiceGroupDeleted,
                setIsServiceGroupChanged
            );

            const { errors: validationErrors, item } = validate(serviceOrganizationSettingAddEditSchema, itemToSave);
            let updatedErrors = validationErrors;
            let hasErrors = false;

            updatedErrors = validateServiceGroupSettingId(
                serviceOrganizationSetting.service.code,
                currentServiceGroup,
                hasServiceProviders,
                validationErrors
            );
            updatedErrors = validateGroupHistoriesTimeOverlap(
                itemToSave.serviceOrganizationGroupHistory,
                updatedErrors
            );

            const embloomItemToSave = {
                embloomQuestionnaireSelection: embloomQuestionnaires.map(
                    (questionnaire) => questionnaire.embloomQuestionnaire.id
                ),
            };
            setErrors(updatedErrors);
            const { errors: embloomValidationErrors, item: embloomItem } = validate(
                embloomQuestionnaireServiceOrganizationSettingSchema,
                embloomItemToSave
            );
            if (updatedErrors.size > 0 || embloomValidationErrors.size > 0) {
                hasErrors = true;
            }
            if (!hasErrors && isGroupHistoriesChanged && !showServiceOrganizationGroupSettingChangedOrDeleteModal) {
                setShowServiceOrganizationGroupSettingChangedOrDeleteModal(true);
            } else if (!hasErrors) {
                await fetchJson(`/api/serviceOrganizationSettings/${serviceOrganizationSetting.id}`, 'POST', item);
                await fetchJson(
                    `/api/serviceServiceOrganizationSettings/${serviceOrganizationSetting.id}/embloomQuestionnaireServiceOrganizationSettings`,
                    'POST',
                    embloomItem
                );
                await reloadServiceOrganizationSettings();
                setErrors(emptyValidationErrors);
                setMode(ServiceOrganizationSettingFormMode.read);
            }
            setIsSaving(false);
        }
    }

    function createServiceOrganizationSettingToSave() {
        if (serviceOrganizationSettingEditVersion) {
            const {
                assetValidation,
                canPerformGlaucoomExamination,
                canReferPatientWithoutSmartphone,
                emailAddressOrganizationInbox,
                emailAddressOrganizationInboxAlt,
                endDate,
                isRequestRecipient,
                isUsingHideRequestedExaminationTaskFeature,
                ksyosHolterAnalystOrganizationId,
                optoConnectPracticeId,
                organization,
                receiveZorgmailOnConsultOpen,
                recipientIsOwnOrganization,
                sendBittiumRecordingsTo,
                sendNotificationOrganizationInbox,
                serviceId,
                serviceGraderPoolId,
                serviceOrganizationGroupHistory,
                somnologyOrganizationType,
                startDate,
                zorgmailNummer,
            } = serviceOrganizationSettingEditVersion;

            return {
                assetValidation,
                canPerformGlaucoomExamination,
                canReferPatientWithoutSmartphone,
                emailAddressOrganizationInbox,
                emailAddressOrganizationInboxAlt,
                endDate,
                isRequestRecipient,
                isSendingDeviceToPatientsAddress: serviceOrganizationSetting.isSendingDeviceToPatientsAddress,
                isUsingHideRequestedExaminationTaskFeature,
                ksyosHolterAnalystOrganizationId,
                optoConnectPracticeId,
                organizationId: organization.id,
                receiveZorgmailOnConsultOpen,
                recipientIsOwnOrganization,
                sendBittiumRecordingsTo,
                sendNotificationOrganizationInbox,
                serviceGraderPoolId,
                serviceId,
                serviceOrganizationGroupHistory,
                somnologyOrganizationType,
                startDate,
                zorgmailNummer,
            };
        }

        return {};
    }

    function createServiceOrganizationSettingEditVersion(
        serviceOrganizationSettingToEdit: IServiceOrganizationSettings
    ) {
        return {
            ...serviceOrganizationSettingToEdit,
            serviceOrganizationSettingsHistory: serviceOrganizationSettingToEdit.serviceOrganizationGroupHistory.map(
                (sogh) => ({ ...sogh, isNew: false })
            ),
        };
    }
}
